import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useKegiatanList, useTahapList, usePanitiaList, usePesertaList } from '@/hooks/useKegiatan'
import { useAuthStore } from '@/stores/authStore'
import FilterChip from '@/components/ui/FilterChip'
import EmptyState from '@/components/ui/EmptyState'
import KegiatanCard from '@/components/kegiatan/KegiatanCard'
import StatCard from '@/components/ui/StatCard'
import type { KegiatanStatusFilter } from '@/stores/kegiatanStore'
import type { Kegiatan } from '@/types/pocketbase'

const filters: { label: string; value: KegiatanStatusFilter }[] = [
  { label: 'Semua', value: 'all' },
  { label: 'Aktif', value: 'aktif' },
  { label: 'Selesai', value: 'selesai' },
  { label: 'Draft', value: 'draft' },
]

export default function DashboardPage() {
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.role)
  const isSuperAdmin = role === 'super_admin'
  const [filter, setFilter] = React.useState<KegiatanStatusFilter>('all')
  const [search, setSearch] = React.useState('')

  const { data, isLoading } = useKegiatanList({
    status: filter === 'all' ? undefined : filter,
    search: search || undefined,
  })

  const items = data?.items || []
  const total = items.length
  const aktif = items.filter((k) => k.status === 'aktif').length
  const selesai = items.filter((k) => k.status === 'selesai').length
  const draft = items.filter((k) => k.status === 'draft').length

  return (
    <div className="py-4 space-y-4">
      {isSuperAdmin && (
        <div className="grid grid-cols-3 gap-2">
          <StatCard number={total} label="Total" color="primary" />
          <StatCard number={aktif} label="Aktif" color="success" />
          <StatCard number={selesai} label="Selesai" color="info" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <FilterChip
            key={f.value}
            label={f.label}
            active={filter === f.value}
            onClick={() => setFilter(f.value)}
          />
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-card h-32 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Belum ada kegiatan"
          description={
            isSuperAdmin
              ? 'Buat kegiatan pertama Anda dengan menekan tombol + di bawah.'
              : 'Belum ada kegiatan dalam filter ini.'
          }
        />
      ) : (
        <div>
          {items.map((k) => (
            <KegiatanCard
              key={k.id}
              kegiatan={k as unknown as Kegiatan}
              pesertaCount={0}
              selesaiCount={0}
            />
          ))}
          {data && data.totalPages > 1 && (
            <p className="text-center text-xs text-text-secondary py-4">
              Menampilkan {items.length} dari {data.totalItems} kegiatan
            </p>
          )}
        </div>
      )}

      {isSuperAdmin && (
        <button
          onClick={() => navigate('/kegiatan/new')}
          className="fixed bottom-20 right-4 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-800 transition-colors z-40"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
    </div>
  )
}
